import { create } from "zustand";
import type { Item } from "@egalregal/shared";
import { getMirroredPlacements } from "../search/catalogStore";
import {
  add as addPlacement,
  placementKey,
  remove as removePlacement,
  setTotal as setPlacementTotal,
  undoLast as undoPlacement,
  type PlacementKey,
  type PlacementOutcome,
  type PlacementUndo,
} from "./placements";

/**
 * The placement slice (placement-ledger 02): optimistic totals for the writes
 * in flight and one undo slot, on top of the ledger's request/mirror path.
 *
 * Not persisted: a reload clears both, and the next server snapshot/mirror is
 * authoritative. The optimistic map keeps the last confirmed server total after
 * a write settles, so a following edit starts from the true total even before
 * the surface re-renders its row.
 */
export interface PlacementsSlice {
  /** In-flight projected totals, then each placement's last saved total. */
  optimistic: Record<PlacementKey, number>;
  /** One-level undo for the last saved write, or null. */
  undo: PlacementUndo | null;
  setTotal(containerId: string, item: Item, quantity: number): Promise<PlacementOutcome>;
  add(containerId: string, item: Item, amount: number): Promise<PlacementOutcome>;
  remove(containerId: string, item: Item): Promise<PlacementOutcome>;
  undoLast(): Promise<PlacementOutcome>;
}

/** The singleton placement slice shared by every editing surface. */
export const usePlacementsStore = create<PlacementsSlice>()((set, get) => {
  /** Show the projected total while the request is in flight. */
  const begin = (key: PlacementKey, projected: number) =>
    set((state) => ({ optimistic: { ...state.optimistic, [key]: projected } }));

  /**
   * Settle one write: a saved total replaces the projection and becomes the
   * undo entry; a failure drops the projection so the row falls back to the
   * stored quantity (the mirror was never written).
   */
  const settle = (
    key: PlacementKey,
    previous: number,
    item: Item,
    outcome: PlacementOutcome,
  ) => {
    if (outcome.status === "saved") {
      set((state) => ({
        optimistic: { ...state.optimistic, [key]: outcome.quantity },
        undo: { key, previous, item },
      }));
      return;
    }
    set((state) => {
      const optimistic = { ...state.optimistic };
      delete optimistic[key];
      return { optimistic };
    });
  };

  /**
   * Run one ledger write through the slice: project its total while the request
   * is in flight, then settle the outcome into the optimistic map and undo slot.
   *
   * @param containerId the holding container.
   * @param item the item being placed.
   * @param projectedTotal the total to show while the request is in flight,
   *   computed from the total the write starts at.
   * @param write the ledger call to await.
   * @returns the ledger's outcome, unchanged.
   */
  const writePlacement = async (
    containerId: string,
    item: Item,
    projectedTotal: (previous: number) => number,
    write: () => Promise<PlacementOutcome>,
  ): Promise<PlacementOutcome> => {
    const key = placementKey(containerId, item.id);
    const previous = totalOf(get().optimistic, containerId, item.id);
    begin(key, projectedTotal(previous));
    const outcome = await write();
    settle(key, previous, item, outcome);
    return outcome;
  };

  return {
    optimistic: {},
    undo: null,

    setTotal: (containerId, item, quantity) =>
      writePlacement(
        containerId,
        item,
        () => (quantity <= 0 ? 0 : quantity),
        () => setPlacementTotal(containerId, item, quantity),
      ),

    add: (containerId, item, amount) =>
      writePlacement(
        containerId,
        item,
        (previous) => previous + amount,
        () => addPlacement(containerId, item, amount),
      ),

    remove: (containerId, item) =>
      writePlacement(
        containerId,
        item,
        () => 0,
        () => removePlacement(containerId, item),
      ),

    undoLast: async () => {
      const entry = get().undo;
      if (!entry) return { status: "saved", quantity: 0 };

      // Consume the slot before writing: the undo is not itself undoable.
      set({ undo: null });
      begin(entry.key, entry.previous);
      const outcome = await undoPlacement(entry);

      if (outcome.status === "saved") {
        set((state) => ({
          optimistic: { ...state.optimistic, [entry.key]: outcome.quantity },
        }));
        return outcome;
      }

      // The server is unchanged, so put the slot back for a retry.
      set((state) => {
        const optimistic = { ...state.optimistic };
        delete optimistic[entry.key];
        return { optimistic, undo: entry };
      });
      return outcome;
    },
  };
});

/** The total a write starts from: the confirmed/optimistic value, else the mirror. */
function totalOf(
  optimistic: Record<PlacementKey, number>,
  containerId: string,
  itemId: string,
): number {
  const key = placementKey(containerId, itemId);
  if (key in optimistic) return optimistic[key]!;
  return (
    getMirroredPlacements().find(
      (placement) => placement.itemId === itemId && placement.containerId === containerId,
    )?.quantity ?? 0
  );
}
